import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Brain,
  CheckCircle2,
  Database,
  Filter,
  GitBranch,
  Layers,
  ListChecks,
  Search,
  Target,
  Users,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";

export const Route = createFileRoute("/admin/plano-inteligencia-leads")({
  head: () => ({
    meta: [
      { title: "Admin — Inteligência de Leads" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/plano-inteligencia-leads">
      <PlanoInteligenciaLeads />
    </AdminGate>
  ),
});

function Section({
  icon: Icon,
  title,
  kicker,
  children,
  tone = "default",
}: {
  icon: typeof Brain;
  title: string;
  kicker?: string;
  children: React.ReactNode;
  tone?: "default" | "success" | "warning";
}) {
  const border =
    tone === "success"
      ? "border-emerald-400/30"
      : tone === "warning"
        ? "border-amber-400/30"
        : "border-white/10";
  const iconTone =
    tone === "success"
      ? "text-emerald-300"
      : tone === "warning"
        ? "text-admin-accent"
        : "text-sky-300";
  return (
    <div className={`rounded-2xl border ${border} bg-white/[0.03] p-6 sm:p-7`}>
      <div className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5">
          <Icon className={`h-5 w-5 ${iconTone}`} />
        </div>
        <div className="min-w-0">
          {kicker && (
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
              {kicker}
            </div>
          )}
          <h2 className="text-lg font-bold text-white">{title}</h2>
        </div>
      </div>
      <div className="mt-5 space-y-3 text-sm leading-relaxed text-white/70">{children}</div>
    </div>
  );
}

function Item({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2.5">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300/80" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
      <div className="text-2xl font-extrabold text-white">{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-white/50">{label}</div>
    </div>
  );
}

function PlanoInteligenciaLeads() {
  return (
    <div className="relative min-h-screen overflow-hidden">
      <div
        className="admin-plan-vignette pointer-events-none absolute inset-0"
      />
      <div className="relative px-4 py-12">
        <div className="mx-auto max-w-5xl">
          {/* Header */}
          <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-admin-accent">
                Em andamento
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-white">
                Inteligência de Leads
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-white/60">
                Sistema de atribuição que responde duas perguntas: <strong className="text-white/85">de
                onde vêm</strong> as pessoas que compram (ORIGEM) e <strong className="text-white/85">o que
                fecha</strong> a venda (DECISÃO).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin/inteligencia-leads"
                className="rounded-lg border border-[#f5b022] bg-[#f5b022] px-3 py-2 text-xs font-semibold text-[#13123a] transition hover:bg-[#e09f17]"
              >
                Abrir dashboard
              </Link>
              <Link
                to="/admin/planos"
                className="inline-flex items-center rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/10"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
              </Link>
            </div>
          </div>

          {/* Números */}
          <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric value="60.271" label="Contatos RD importados" />
            <Metric value="+3.200" label="Pessoas únicas na base própria" />
            <Metric value="Ilimitada" label="Janela de ORIGEM" />
            <Metric value="42 dias" label="Janela de DECISÃO" />
          </div>

          <div className="space-y-5">
            <Section icon={Target} title="O problema que resolvemos" kicker="Contexto">
              <p>
                Antes, cada funil era avaliado isoladamente: o lead entrava, a venda acontecia e
                ninguém sabia qual conteúdo realmente construiu a decisão. Funis de topo (quizzes,
                aulas experimentais, materiais gratuitos) apareciam como "sem venda", enquanto o
                pré-checkout levava todo o crédito — mesmo tendo sido apenas o último clique.
              </p>
              <p>
                A consequência prática: risco de cortar investimento no que gera demanda e
                superinvestir no que só coleta a venda já madura.
              </p>
            </Section>

            <Section icon={GitBranch} title="Como o modelo funciona" kicker="Metodologia" tone="success">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-sky-400/25 bg-sky-400/5 p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-sky-300">
                    ORIGEM · primeiro toque
                  </div>
                  <p className="mt-2 text-sm text-white/70">
                    O primeiro funil em que a pessoa apareceu, sem limite de tempo. Responde:{" "}
                    <strong className="text-white/85">quem traz gente nova para a base?</strong> É a
                    métrica para decidir onde colocar mídia de aquisição.
                  </p>
                </div>
                <div className="rounded-xl border border-amber-400/25 bg-amber-400/5 p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-admin-accent">
                    DECISÃO · último toque (42 dias)
                  </div>
                  <p className="mt-2 text-sm text-white/70">
                    O último funil tocado até 42 dias antes da compra. Responde:{" "}
                    <strong className="text-white/85">o que converte quem já conhece a FEO?</strong> É
                    a métrica para decidir onde colocar remarketing e ofertas.
                  </p>
                </div>
              </div>
              <p>
                <strong className="text-white/85">Por que 42 dias:</strong> é o ciclo médio observado
                entre a última interação de conteúdo e o pagamento nas pós. Janelas maiores começam a
                creditar funis que já não influenciaram a decisão; menores descartam nutrição
                legítima.
              </p>
              <p>
                Abandonamos o modelo multi-touch com pesos fracionados: ele diluía o crédito e não
                gerava nenhuma decisão prática de investimento. Também removemos receita e ROAS do
                dashboard — trabalhamos com <strong className="text-white/85">número de vendas por
                lead/funil</strong>, que é o dado confiável hoje.
              </p>
            </Section>

            <Section icon={Database} title="O que já está construído" kicker="Entregas">
              <Item>
                <strong className="text-white/85">Identidade unificada de pessoa</strong> — e-mail e
                telefone (E.164) normalizados unem capturas diferentes da mesma pessoa em uma única
                identidade, com todos os pontos de contato em ordem cronológica.
              </Item>
              <Item>
                <strong className="text-white/85">Reconstrução automática da atribuição</strong> — a
                rotina no banco recalcula ORIGEM e DECISÃO cruzando capturas, funis e vendas
                confirmadas da Eduzz.
              </Item>
              <Item>
                <strong className="text-white/85">Base histórica do RD Station importada</strong> —
                60.271 contatos (nov/2021 a hoje) com tags, data e origem da primeira conversão. Isso
                fechou o buraco de "venda sem origem conhecida": praticamente toda identidade atual
                tem histórico.
              </Item>
              <Item>
                <strong className="text-white/85">Matriz ORIGEM × DECISÃO por funil</strong> — cada
                funil aparece com quantas vendas originou e quantas fechou, permitindo ver quem é
                gerador de demanda e quem é fechador.
              </Item>
              <Item>
                <strong className="text-white/85">Separação por tipo de produto</strong> — vendas
                divididas entre Pós-graduações e Outros produtos (certificações, Star, imersões,
                e-books), porque a jornada e o ticket são completamente diferentes.
              </Item>
              <Item>
                <strong className="text-white/85">Filtro CRM × MKT</strong> — separa venda direta
                feita pela equipe no WhatsApp da venda que aconteceu sozinha no checkout.
              </Item>
              <Item>
                <strong className="text-white/85">Drill-down</strong> — todo número da tabela abre a
                lista de pessoas por trás dele. Nada é caixa-preta.
              </Item>
              <Item>
                <strong className="text-white/85">Pesquisa por pessoa e por lote</strong> — consulta
                individual com jornada completa, ou vários e-mails/telefones de uma vez (separados por
                vírgula ou colados de planilha).
              </Item>
              <Item>
                <strong className="text-white/85">Dashboard enxuto</strong> — duas abas (Funis e
                Pessoas). As abas de investimento e banco foram removidas por não gerarem decisão.
              </Item>
            </Section>

            <Section icon={Layers} title="Como usar no dia a dia" kicker="Operação">
              <Item>
                <strong className="text-white/85">Reunião de mídia:</strong> ordene por ORIGEM. Os
                funis do topo da lista merecem mais verba de aquisição — são eles que abastecem a
                base.
              </Item>
              <Item>
                <strong className="text-white/85">Reunião de oferta/campanha:</strong> ordene por
                DECISÃO. Esses são os ativos que fecham venda — priorize-os em remarketing, e-mail e
                disparo de campanha.
              </Item>
              <Item>
                <strong className="text-white/85">Funil com ORIGEM alta e DECISÃO baixa:</strong> gera
                demanda mas não converte sozinho. Não corte — conecte a um funil fechador.
              </Item>
              <Item>
                <strong className="text-white/85">Funil com DECISÃO alta e ORIGEM baixa:</strong> vive
                de audiência já existente. Escalar mídia nele tende a decepcionar.
              </Item>
              <Item>
                <strong className="text-white/85">Antes de ligar para um lead:</strong> pesquise a
                pessoa e veja a jornada — por onde entrou, o que consumiu, o que já comprou.
              </Item>
            </Section>

            <Section icon={ListChecks} title="Próximos passos sugeridos" kicker="Roadmap" tone="warning">
              <Item>
                <strong className="text-white/85">Validação com a equipe (agora):</strong> conferir
                junto do comercial se o ranking de DECISÃO bate com a percepção de quem atende. É o
                último check antes de considerar o plano concluído.
              </Item>
              <Item>
                <strong className="text-white/85">Recorrência da reconstrução:</strong> agendar o
                recálculo automático diário, para o dashboard nunca depender de ação manual.
              </Item>
              <Item>
                <strong className="text-white/85">Comparativo de períodos:</strong> ver o mesmo funil
                mês a mês para detectar desgaste de criativo ou saturação de audiência.
              </Item>
              <Item>
                <strong className="text-white/85">Tempo médio até a compra por funil:</strong> saber
                quais funis vendem rápido e quais exigem nutrição longa, para calibrar expectativa de
                campanha.
              </Item>
              <Item>
                <strong className="text-white/85">Camada de investimento (opcional):</strong> se um dia
                quisermos custo por venda por funil, basta plugar o gasto de mídia já registrado — o
                modelo de atribuição está pronto para isso.
              </Item>
              <Item>
                <strong className="text-white/85">Cruzamento com vendedora:</strong> ver quais origens
                de lead cada vendedora converte melhor, para distribuição mais inteligente.
              </Item>
            </Section>

            <Section icon={Filter} title="Limites conhecidos" kicker="Transparência">
              <p>
                <strong className="text-white/85">Nem toda venda tem origem mapeada.</strong> Compras
                de pessoas que nunca passaram por um funil rastreado (indicação, orgânico direto)
                aparecem sem origem — e isso é informação, não erro.
              </p>
              <p>
                <strong className="text-white/85">O RD histórico traz origem, não funil interno.</strong>{" "}
                Para contatos antigos temos a origem declarada no RD; o detalhamento por funil só
                existe a partir das capturas feitas no nosso site.
              </p>
              <p>
                <strong className="text-white/85">Vendas recorrentes/renovações são ignoradas</strong>{" "}
                na contagem, para não inflar o resultado dos funis com faturas repetidas.
              </p>
            </Section>

            <div className="grid gap-5 sm:grid-cols-2">
              <Section icon={Users} title="Onde ver" kicker="Atalhos">
                <div className="space-y-2">
                  <Link to="/admin/inteligencia-leads" className="block text-sky-300 hover:underline">
                    /admin/inteligencia-leads — dashboard (Funis e Pessoas)
                  </Link>
                  <Link to="/admin/leads" className="block text-sky-300 hover:underline">
                    /admin/leads — base operacional de leads e compras
                  </Link>
                  <Link to="/admin/acoes-marketing" className="block text-sky-300 hover:underline">
                    /admin/acoes-marketing — histórico de ações e campanhas
                  </Link>
                </div>
              </Section>
              <Section icon={Search} title="Para apresentar" kicker="Resumo em 3 frases">
                <p>1. Agora sabemos quem <strong className="text-white/85">traz</strong> a pessoa e quem <strong className="text-white/85">fecha</strong> a venda — separadamente.</p>
                <p>2. Toda pessoa da base tem uma jornada auditável, com 5 anos de histórico do RD incorporados.</p>
                <p>3. A decisão de investimento passa a ser feita sobre número de vendas por funil, não sobre percepção.</p>
              </Section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
